/**
 * "The confirmation link was just opened" -- passed between tabs of this app.
 *
 * THE FRICTION IT REMOVES. The sign-up tab waits on /verify-email with the
 * password the user typed still in memory. The email link opens in a SECOND
 * tab (/auth/action), which redeems the code and then has nothing to do but
 * offer "Continue to sign in" -- a login form, so the user retypes the password
 * they typed a minute ago, while the first tab sits there until they remember
 * to come back and press "I've confirmed my email".
 *
 * HOW. The action tab broadcasts that it redeemed a verification code, and the
 * waiting tab reacts by running the same server check the button runs. That
 * check then signs in with the credentials the waiting tab already holds.
 *
 * WHAT THE MESSAGE IS NOT. It carries nothing: no uid, no email, no token, and
 * of course no password. It is only a prompt to ask the server, and the server
 * is what decides whether the account is verified. A message forged by another
 * script on this origin can do no more than trigger one extra check.
 *
 * LIMITS. BroadcastChannel is same-origin and same-browser, so a link opened on
 * a different device or browser does not reach the waiting tab this way. That
 * case is covered by the tab re-checking when it regains focus
 * (see useAutoVerifyEmail), and by the manual button.
 */

const CHANNEL_NAME = 'biasafe-email-verified';

/** Tell any waiting sign-up tab that a verification link was redeemed. */
export function announceEmailVerified(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage('verified');
    channel.close();
  } catch {
    // A browser that blocks the channel just falls back to the focus check.
  }
}

/** Subscribe to the announcement. Returns the unsubscribe function. */
export function onEmailVerified(callback: () => void): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = () => callback();
    return () => channel.close();
  } catch {
    return () => undefined;
  }
}
